import React from "react";
import { Navigate, Route, Routes, useLocation } from "react-router";
import App from "../../App";

import { ROUTES } from "./routes";
import { Home } from "../../views/home/ui/home";
import { Search } from "../../views/search/ui/search";
import { PatientInfoOutlet } from "../../views/patient-info-outlet/ui/patient-info-outlet";
import { Problems } from "../../views/problems/ui/problems";
import { Allergies } from "../../views/allergies/ui/allergies";
import { Medications } from "../../views/medications/ui/medications";
import { Immunizations } from "../../views/immunizations/ui/immunizations";
import { Appointments } from "../../views/appointments/ui/appointments";
import { Documents } from "../../views/documents/ui/documents";
import { DicomStudies } from "../../views/dicom-studies/ui/dicom-studies";
import { Encounters } from "../../views/encounters/ui/encounters";
import { Flowsheets } from "../../views/flowsheets/ui/flowsheets";
import { FlowsheetDetail } from "../../views/flowsheets/ui/flowsheet-detail";
import { EncountersScannedDocs } from "../../views/encounters/ui/encounters-scanned-docs";
import { EncountersHistoryReport } from "../../views/encounters/ui/encounters-history-report";
import { EncountersLabResults } from "../../views/encounters/ui/encounters-lab-results";
import { EncountersBillingInfo } from "../../views/encounters/ui/encounters-billing-info";
import { Demographics } from "../../views/demographics/demographics";
import { Insurance } from "../../views/insurance/ui/insurance";
import { OrdersProcedures } from "../../views/orders-procedures/ui/orders-procedures";
import { OrdersProceduresDetail } from "../../views/orders-procedures/ui/orders-procedures-detail";
import { PhysicianCareTeam } from "../../views/physician-care-team/ui/physician-care-team";
import { Vitals } from "../../views/vitals/ui/vitals";
import { Results } from "../../views/results/ui/results";
import { ResultsDetail } from "../../views/results/ui/results-detail";
import { Summary } from "../../views/summary/ui/summary";
import { EncountersLabResultsDetails } from "../../views/encounters/ui/encounters-lab-results-details/encounters-lab-results-details";
import { EncountersLabResultsProcedureDetails } from "../../views/encounters/ui/encounters-lab-results-details/encounters-lab-results-procedure-details";
import { RoiRequests } from "../../views/roi/ui/roi-requests";
import { RoiRequestView } from "../../views/roi/ui/roi-request-view";
import { SignInPage } from "../../features/auth/ui/sign-in-page";
import { CreatePasswordPage } from "../../features/auth/ui/create-password-page";
import { ForgotPasswordPage } from "../../features/auth/ui/forgot-password-page";
import { NeedHelpPage } from "../../features/auth/ui/need-help-page";
import { getAuthSession } from "../../features/auth/model/auth.storage";

const PrivateRoute = ({ children }: { children: React.ReactElement }) => {
  const location = useLocation();

  if (!getAuthSession()) {
    return <Navigate to={ROUTES.SIGN_IN} replace state={{ from: location }} />;
  }

  return children;
};

export const BrowserRoutes = () => {
  return (
    <Routes>
      <Route path={ROUTES.SIGN_IN} element={<SignInPage />} />
      <Route path={ROUTES.CREATE_PASSWORD} element={<CreatePasswordPage />} />
      <Route path={ROUTES.FORGOT_PASSWORD} element={<ForgotPasswordPage />} />
      <Route path={ROUTES.NEED_HELP} element={<NeedHelpPage />} />
      <Route
        path={ROUTES.APP}
        element={
          <PrivateRoute>
            <App />
          </PrivateRoute>
        }
      >
        <Route index element={<Home />} />
        <Route path={ROUTES.SEARCH} element={<Search />} />
        <Route path={ROUTES.PATIENT_STATS} element={<PatientInfoOutlet />}>
          <Route index element={<Problems />} />
          <Route
            path={ROUTES.PATIENT_DETAILS_ALLERGIES.replace(
              ":patientID",
              ":patientID",
            )}
            element={<Allergies />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_MEDICATIONS.replace(
              ":patientID",
              ":patientID",
            )}
            element={<Medications />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_IMMUNIZATIONS.replace(
              ":patientID",
              ":patientID",
            )}
            element={<Immunizations />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_APPOINTMENTS}
            element={<Appointments />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_DOCUMENTS.replace(
              ":patientID",
              ":patientID",
            )}
            element={<Documents />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_DICOM_STUDIES}
            element={<DicomStudies />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS}
            element={<Encounters />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS_HISTORY_REPORT}
            element={<EncountersHistoryReport />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT}
            element={<EncountersLabResults />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT_DETAILS}
            element={<EncountersLabResultsDetails />}
          />
          <Route
            path={
              ROUTES.PATIENT_DETAILS_ENCOUNTERS_LABS_REPORT_PROCEDURE_DETAILS
            }
            element={<EncountersLabResultsProcedureDetails />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS_BILLING_REPORT}
            element={<EncountersBillingInfo />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ENCOUNTERS_SCANNED_DOCS}
            element={<EncountersScannedDocs />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_FLOWSHEETS}
            element={<Flowsheets />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_FLOWSHEET_DETAILS}
            element={<FlowsheetDetail />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_DEMOGRAPHICS}
            element={<Demographics />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_INSURANCE}
            element={<Insurance />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ORDERS_PROCEDURES}
            element={<OrdersProcedures />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ORDERS_PROCEDURES_DETAILS}
            element={<OrdersProceduresDetail />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_PHYSICIAN_CARE_TEAM}
            element={<PhysicianCareTeam />}
          />
          <Route path={ROUTES.PATIENT_DETAILS_RESULTS} element={<Results />} />
          <Route
            path={ROUTES.PATIENT_DETAILS_RESULT_DETAILS}
            element={<ResultsDetail />}
          />
          <Route path={ROUTES.PATIENT_DETAILS_SUMMARY} element={<Summary />} />
          <Route path={ROUTES.PATIENT_DETAILS_VITALS} element={<Vitals />} />
          <Route
            path={ROUTES.PATIENT_DETAILS_ROI_REPORTS}
            element={<RoiRequests />}
          />
          <Route
            path={ROUTES.PATIENT_DETAILS_ROI_REQUEST_VIEW}
            element={<RoiRequestView />}
          />
        </Route>
      </Route>
    </Routes>
  );
};
